import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

interface GroupAvatarProps {
  name: string;
  avatarUrl?: string | null;
  icon?: string | null;
  className?: string;
  fallbackClassName?: string;
  size?: "sm" | "md" | "lg" | "xl";
}

const sizeClasses = {
  sm: "h-7 w-7",
  md: "h-8 w-8",
  lg: "h-10 w-10",
  xl: "h-14 w-14",
};

const fallbackFontSizes = {
  sm: "text-[11px]",
  md: "text-xs",
  lg: "text-sm",
  xl: "text-base",
};

const iconFontSizes = {
  sm: "text-sm",
  md: "text-base",
  lg: "text-lg",
  xl: "text-2xl",
};

const getInitials = (name: string): string => {
  const trimmed = name.trim();
  if (!trimmed) return "?";
  const parts = trimmed.split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] || "").toUpperCase() + (parts[parts.length - 1][0] || "").toUpperCase();
  }
  return trimmed.substring(0, 2).toUpperCase();
};

export const GroupAvatar = ({
  name,
  avatarUrl,
  icon,
  className,
  fallbackClassName,
  size = "md",
}: GroupAvatarProps) => {
  const sizeClass = sizeClasses[size];
  const fallbackFontSize = icon ? iconFontSizes[size] : fallbackFontSizes[size];
  const initials = getInitials(name);

  return (
    <Avatar className={cn(sizeClass, className)}>
      <AvatarImage
        src={avatarUrl || undefined}
        alt={name}
        loading="lazy"
      />
      <AvatarFallback
        className={cn(
          "bg-gradient-to-br from-primary/20 to-secondary/20 text-foreground font-semibold",
          fallbackFontSize,
          fallbackClassName
        )}
      >
        {icon || initials}
      </AvatarFallback>
    </Avatar>
  );
};
